// src/components/Admin/ProjectNameManager.js
import React, { useState, useEffect } from 'react';
import { db } from '../../firebase'; // Import db from your firebase.js
import { collection, addDoc, getDocs, deleteDoc, doc, query, orderBy } from 'firebase/firestore';

function ProjectNameManager() {
  const [newProjectName, setNewProjectName] = useState('');
  const [projectNames, setProjectNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const projectNamesCollectionRef = collection(db, 'project_names');

  // Function to fetch project names
  const fetchProjectNames = async () => {
    try {
      setLoading(true); // Set loading to true when fetching starts
      const q = query(projectNamesCollectionRef, orderBy('name')); // Order alphabetically
      const data = await getDocs(q);
      setProjectNames(data.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
      setLoading(false);
    } catch (err) {
      console.error("Error fetching project names:", err);
      setError("Failed to load project names. Please try again.");
      setLoading(false);
    }
  };

  // Fetch project names from Firestore on component mount
  useEffect(() => {
    fetchProjectNames();
  }, []); // Empty dependency array means this runs once on mount

  const handleAddProjectName = async (e) => {
    e.preventDefault();
    if (!newProjectName.trim()) {
      alert("Project name cannot be empty.");
      return;
    }
    try {
      // Check if project name already exists (case-insensitive)
      const existingProjectName = projectNames.find(pn => pn.name.toLowerCase() === newProjectName.trim().toLowerCase());
      if (existingProjectName) {
        alert(`Project name '${newProjectName.trim()}' already exists.`);
        setNewProjectName('');
        return;
      }

      await addDoc(projectNamesCollectionRef, { name: newProjectName.trim() });
      setNewProjectName('');
      await fetchProjectNames(); // Re-fetch project names to update the list
    } catch (err) {
      console.error("Error adding project name:", err);
      setError("Failed to add project name. Please ensure you are logged in as an admin and have permissions.");
    }
  };

  const handleDeleteProjectName = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete '${name}'? This cannot be undone.`)) {
      try {
        const projectNameDoc = doc(db, 'project_names', id);
        await deleteDoc(projectNameDoc);
        setProjectNames(projectNames.filter(pn => pn.id !== id)); // Remove from local state
      } catch (err) {
        console.error("Error deleting project name:", err);
        setError("Failed to delete project name. Please ensure you are logged in as an admin and have permissions.");
      }
    }
  };

  // --- Rendering part ---
  if (loading) return <p style={messageStyle}>Loading project names...</p>;
  if (error) return <p style={errorStyle}>Error: {error}</p>;

  return (
    <div style={containerStyle}>
      <h2 style={headingStyle}>Manage Project Names</h2>
      <form onSubmit={handleAddProjectName} style={formStyle}>
        <input
          type="text"
          placeholder="New project name"
          value={newProjectName}
          onChange={(e) => setNewProjectName(e.target.value)}
          style={inputStyle}
        />
        <button type="submit" style={buttonStyle}>Add Project Name</button>
      </form>

      <div style={listContainerStyle}>
        {projectNames.length === 0 ? (
          <p style={messageStyle}>No project names added yet.</p>
        ) : (
          <ul style={listStyle}>
            {projectNames.map((projectName) => (
              <li key={projectName.id} style={listItemStyle}>
                <span>{projectName.name}</span> {/* Display the project name */}
                <button onClick={() => handleDeleteProjectName(projectName.id, projectName.name)} style={deleteButtonStyle}>Delete</button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// Reusing the same basic styles from LocationManager for consistency
const containerStyle = {
  maxWidth: '600px',
  margin: '20px auto',
  padding: '20px',
  border: '1px solid #ddd',
  borderRadius: '8px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  fontFamily: 'Arial, sans-serif',
  backgroundColor: '#f9f9f9',
};

const headingStyle = {
  textAlign: 'center',
  color: '#333',
  marginBottom: '20px',
};

const formStyle = {
  display: 'flex',
  gap: '10px',
  marginBottom: '20px',
};

const inputStyle = {
  flexGrow: '1',
  padding: '10px',
  border: '1px solid #ccc',
  borderRadius: '4px',
  fontSize: '16px',
};

const buttonStyle = {
  padding: '10px 15px',
  backgroundColor: '#4CAF50',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '16px',
};

const listContainerStyle = {
  maxHeight: '400px',
  overflowY: 'auto',
  borderTop: '1px solid #eee',
  paddingTop: '15px',
};

const listStyle = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
};

const listItemStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '10px 0',
  borderBottom: '1px dashed #eee',
  fontSize: '16px',
  color: '#555',
};

const deleteButtonStyle = {
  backgroundColor: '#f44336',
  color: 'white',
  border: 'none',
  padding: '5px 10px',
  borderRadius: '3px',
  cursor: 'pointer',
  fontSize: '14px',
};

const messageStyle = {
  textAlign: 'center',
  padding: '10px',
  color: '#666',
};

const errorStyle = {
  textAlign: 'center',
  padding: '10px',
  color: 'red',
  fontWeight: 'bold',
};

export default ProjectNameManager;